import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui";
import { Input } from "@/components/ui/input";
import {
  AlertCircle,
  AlertCircleIcon,
  FileTextIcon,
  UploadIcon,
} from "lucide-react";
import { useFileSelectorController } from "./useFileSelectorController";
import { Show } from "@/components/core";
import { cn } from "@/lib/utils";

export interface FileSelectorProps {
  selectedTemplateId?: number;
  onFileSelected: (file?: File) => void;
}

export const FileSelector = (props: FileSelectorProps) => {
  const controller = useFileSelectorController(props);

  const [isDragging, setIsDragging] = useState(false);
  const isDisabled = !props.selectedTemplateId;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDisabled) return;

    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isDisabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      // if (validateFile(droppedFile)) {
      controller.selectFile(droppedFile);
      // }
    }
  };

  return (
    <div className="relative">
      <h2 className="text-lg font-semibold mb-2">2. Upload File</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Select a CSV or Excel file to import
      </p>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-6">
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
                controller.error ? "border-destructive/50 bg-destructive/5" : "",
                isDisabled ? "opacity-60 cursor-not-allowed" : ""
              )}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              <>
                <Show if={controller.file !== undefined}>
                  <div className="text-center mb-2">
                    <FileTextIcon className="h-10 w-10 text-muted-foreground mb-2 mx-auto" />
                    <p className="font-medium">{controller.file?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {controller.file?.size
                        ? (controller.file?.size / 1024).toFixed(2)
                        : 0}
                      KB
                    </p>
                  </div>
                </Show>

                <Show if={controller.file === undefined}>
                  <UploadIcon className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="font-medium mb-1">
                    Drag and drop your file here
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Supports CSV and Excel files up to 10MB
                  </p>
                </Show>
                <Button type="button" variant="secondary" asChild disabled={isDisabled} aria-disabled={isDisabled}>
                  <label>
                    <Input
                      type="file"
                      className="hidden"
                      accept=".csv,.xlsx,.xls"
                      disabled={isDisabled}
                      onChange={(e) => {
                        if (isDisabled) return;
                        if (e.target.files && e.target.files[0]) {
                          controller.selectFile(e.target.files[0]);
                        }
                      }}
                    />
                    Browse Files
                  </label>
                </Button>
              </>
            </div>

            {controller.error && (
              <div className="flex items-center mt-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 mr-1" />
                {controller.error}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overlay for when no template is selected */}
      {!props.selectedTemplateId && (
        <div
          className="absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg z-10"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="text-center p-6 max-w-md">
            <AlertCircleIcon className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Template Required</h3>
            <p className="text-muted-foreground mb-4">
              Please select a template from the list above before uploading a
              file.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
